import React from 'react';
import clsx from 'clsx';
import { AccentToken } from '../../theme/accents';

interface CardFooterProps {
  /** Left-aligned content, typically one or more StatusDots + StatusDivider. */
  status: React.ReactNode;
  /** Right-aligned content: timestamp, refresh button, toggle switch, etc. */
  controls?: React.ReactNode;
  /** Accent token — tints the footer to match the card. Defaults to neutral. */
  accent?: AccentToken;
  className?: string;
}

/**
 * The bordered footer bar shared by every entity card. Holds status indicators
 * on the left and controls on the right. The pinned-to-bottom behavior (mt-auto)
 * lets footers line up across a grid row regardless of body height.
 *
 * The footer is neutral for every accent: it draws its border and tint from
 * Semi's `--semi-color-*` variables so it matches the surface above it in both
 * themes. It used to be accent-tinted, which turned a board of mixed entity
 * types into a row of differently colored panels (see theme/accents.ts).
 */
const CardFooter: React.FC<CardFooterProps> = ({
  status,
  controls,
  className,
}) => {
  return (
    <div
      className={clsx(
        // rounded-b-xl tracks CardShell's rounded-xl. The shell cannot use
        // overflow-hidden to clip this corner because cards open dropdowns and
        // popovers that must escape the card box, so the footer rounds itself.
        'refined-card-footer mt-auto px-5 py-4 border-t rounded-b-xl',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">{status}</div>
        {controls && <div className="flex items-center gap-3">{controls}</div>}
      </div>
    </div>
  );
};

export default CardFooter;
